import { type FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2, Lock, NotebookPen } from "lucide-react";

import { Input } from "@components/ui/Input";

import {
  formatReceivedOn,
  NOTICE_TYPE_LABELS,
  noticeTypeLabel,
  safeHttpUrl,
  todayIsoDate,
} from "../../queries/users/notices";
import {
  type AdminUserNote,
  type AdminUserNotice,
  createAdminUserNote,
  listAdminUserNotes,
  NOTICE_TYPES,
  type NoticeType,
} from "../../queries/users/userApi";
import { Loading } from "../layout/Loading";

const SELECT_CLASS =
  "min-h-10 rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm";

export const noteAnchorId = (noteId: number) => `admin-note-${noteId}`;

const authorLabel = (note: AdminUserNote) =>
  note.authorEmail ?? `Admin ${note.authorSub.slice(0, 8)}…`;

/**
 * "Notice on file" flags for the user header (OMG-2558). One chip per notice,
 * each linking to the note that recorded it. `undefined` means the API did not
 * say (an omg-api that predates notices), which must read as unknown rather
 * than as "no notice on file".
 */
export const NoticeOnFileBadges = ({
  notices,
}: {
  notices: AdminUserNotice[] | undefined;
}) => {
  if (notices === undefined) {
    return (
      <span
        className="text-xs px-1 border border-gray-400 text-gray-600"
        title="This omg-api does not report customer notices yet (OMG-2558)."
      >
        Notice status unavailable
      </span>
    );
  }
  return (
    <>
      {notices.map((notice) => (
        <a
          key={notice.noteId}
          href={`#${noteAnchorId(notice.noteId)}`}
          className="text-xs font-semibold px-1 text-white bg-red-700 hover:bg-red-800 no-underline"
          title={`Recorded ${format(new Date(notice.recordedAt), "PPpp")} as note #${notice.noteId}`}
        >
          Notice on file: {noticeTypeLabel(notice.type)} · received{" "}
          {formatReceivedOn(notice.receivedOn)}
        </a>
      ))}
    </>
  );
};

/**
 * Append-only admin notes on a user (OMG-2558 Phase 1). There is deliberately
 * no edit or delete control: omg-api has no such route and the table rejects
 * both. A correction is a new note. The author and timestamp are stamped by
 * the server from the signed-in admin; nothing here sends them.
 */
export const AdminNotes = ({
  userId,
  onNoteCreated,
}: {
  userId: string;
  onNoteCreated: (note: AdminUserNote) => void;
}) => {
  const queryClient = useQueryClient();
  const queryKey = ["admin-user-notes", userId] as const;

  const {
    data,
    isLoading,
    isError: loadFailed,
  } = useQuery({
    queryKey,
    queryFn: () => listAdminUserNotes(userId),
    retry: false,
  });

  const [body, setBody] = useState("");
  const [url, setUrl] = useState("");
  const [recordsNotice, setRecordsNotice] = useState(false);
  const [noticeType, setNoticeType] = useState<NoticeType | "">("");
  const [receivedOn, setReceivedOn] = useState(todayIsoDate());
  const [formError, setFormError] = useState("");

  const resetForm = () => {
    setBody("");
    setUrl("");
    setRecordsNotice(false);
    setNoticeType("");
    setReceivedOn(todayIsoDate());
    setFormError("");
  };

  const mutation = useMutation({
    mutationFn: () =>
      createAdminUserNote(userId, {
        body: body.trim(),
        ...(url.trim() && { url: url.trim() }),
        ...(recordsNotice &&
          noticeType && { notice: { type: noticeType, receivedOn } }),
      }),
    onSuccess: (note) => {
      if (data) {
        queryClient.setQueryData<{ notes: AdminUserNote[] }>(queryKey, {
          notes: [note, ...data.notes],
        });
      } else {
        // The list never loaded: writing just this note into the cache would
        // clear the error and show one note as if it were the whole history.
        void queryClient.invalidateQueries({ queryKey });
      }
      resetForm();
      onNoteCreated(note);
    },
    onError: (err) => {
      setFormError(
        err instanceof Error ? err.message : "Failed to save the note.",
      );
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // A second submit would be a second permanent note.
    if (mutation.isPending) return;
    setFormError("");
    if (!body.trim()) {
      setFormError("Note text is required.");
      return;
    }
    if (url.trim() && !safeHttpUrl(url.trim())) {
      setFormError("Link must be a full URL starting with https://.");
      return;
    }
    if (recordsNotice && (!noticeType || !receivedOn)) {
      setFormError("Choose the notice type and the date it was received.");
      return;
    }
    mutation.mutate();
  };

  const notes = data?.notes ?? [];
  const saving = mutation.isPending;

  return (
    <section
      className="mt-8 border-t pt-4"
      aria-labelledby="admin-notes-heading"
    >
      <h3 id="admin-notes-heading" className="flex items-center gap-2">
        <NotebookPen className="w-4 h-4" /> Admin notes
      </h3>

      <form onSubmit={onSubmit} className="mt-2 space-y-2">
        <label htmlFor="admin-note-body" className="text-sm font-medium">
          Note
        </label>
        <textarea
          id="admin-note-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={saving}
          rows={3}
          maxLength={20000}
          className="w-full rounded-md border border-input px-3 py-2 text-sm"
          placeholder="What was received, and what it said"
        />
        <label htmlFor="admin-note-url" className="text-sm font-medium">
          Link (optional)
        </label>
        <Input
          id="admin-note-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={saving}
          placeholder="https:// link to the saved original"
        />

        <label className="flex items-center text-sm font-medium">
          <input
            type="checkbox"
            checked={recordsNotice}
            onChange={(e) => setRecordsNotice(e.target.checked)}
            disabled={saving}
            className="mr-2 h-4 w-4 rounded border-gray-300"
          />
          This note records a customer notice (shows &quot;Notice on file&quot;
          on the account)
        </label>
        {recordsNotice && (
          <div className="flex flex-wrap gap-2">
            <select
              aria-label="Notice type"
              value={noticeType}
              onChange={(e) => setNoticeType(e.target.value as NoticeType | "")}
              disabled={saving}
              className={SELECT_CLASS}
            >
              <option value="">Notice type…</option>
              {NOTICE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {NOTICE_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm">
              Date received
              <input
                type="date"
                value={receivedOn}
                max={todayIsoDate()}
                onChange={(e) => setReceivedOn(e.target.value)}
                disabled={saving}
                className={SELECT_CLASS}
              />
            </label>
          </div>
        )}

        <p className="flex items-center gap-1 text-xs text-gray-600">
          <Lock className="w-3 h-3" /> Notes are permanent: once saved they
          can&apos;t be edited or deleted. To correct one, add a new note.
        </p>
        {formError && <p className="text-sm text-red-600">{formError}</p>}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-70"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
              </>
            ) : (
              "Add permanent note"
            )}
          </button>
        </div>
      </form>

      <div className="mt-4">
        {loadFailed ? (
          <p className="text-red-600">
            Couldn&apos;t load admin notes. Reload the page to try again.
          </p>
        ) : isLoading ? (
          <Loading />
        ) : notes.length === 0 ? (
          <p className="text-gray-500 text-sm">No admin notes yet.</p>
        ) : (
          <ol className="space-y-3">
            {notes.map((note) => {
              const href = safeHttpUrl(note.url);
              return (
                <li
                  key={note.id}
                  id={noteAnchorId(note.id)}
                  className="rounded-md border px-3 py-2"
                >
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                    <span className="font-mono">#{note.id}</span>
                    <span title={`Cognito sub ${note.authorSub}`}>
                      {authorLabel(note)}
                    </span>
                    <span>{format(new Date(note.createdAt), "PPpp")}</span>
                    {note.notice && (
                      <span className="font-semibold px-1 text-white bg-red-700">
                        {noticeTypeLabel(note.notice.type)} · received{" "}
                        {formatReceivedOn(note.notice.receivedOn)}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm whitespace-pre-wrap break-words">
                    {note.body}
                  </p>
                  {note.url &&
                    (href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline break-all"
                      >
                        {note.url}
                      </a>
                    ) : (
                      <span className="text-sm break-all">{note.url}</span>
                    ))}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
};
